import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
  Typography,
} from '@mui/material';
import reqProvider from '../../util/requests/reqProvider';
import NumberTextField from '../../Components/NumberTextField';
import { ControlPoint } from '@mui/icons-material';

interface Modifier {
  id: number | null;
  warehouseProductId: number | null;
  warehouseProductUnitId: string;
  grossPriceDifference: number | null;
  isActive: boolean;
}

interface UnitComponent {
  id: number | null;
  warehouseProductId: number | null;
  warehouseProductUnitId: string;
  quantity: number | null;
  modifiers: Modifier[];
}

interface IngredientsProps {
  open: boolean;
  onClose: () => void;
  unitComponents?: UnitComponent[];
  onUpdate?: (unitComponents: UnitComponent[]) => void;
}

interface Unit {
  id: string;
  unitName: string;
  type: string | null;
}

interface Ingredient {
  id: number;
  name: string;
  units: Unit[];
  tags: any[];
  activeFlag: boolean;
}

const Ingredients: React.FC<IngredientsProps> = ({ open, onClose, unitComponents, onUpdate }) => {
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [selectedIngredient, setSelectedIngredient] = useState<Ingredient | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [quantity, setQuantity] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentUnitComponents, setCurrentUnitComponents] = useState<UnitComponent[]>([]);

  console.log(unitComponents)
  useEffect(() => {
    const fetchComponents = async () => {
      try {
        setLoading(true);
        const response = await reqProvider.get('product/components', {
          posSetId: localStorage.getItem('productPosSetId')
        });
        if (response.data) {
          setIngredients(response.data);
        }
      } catch (error) {
        console.error('Error fetching components:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchComponents();
    }
  }, [open]);

  useEffect(() => {
    if (selectedIngredient) {
      setUnits(selectedIngredient.units);
    } else {
      setUnits([]);
    }
  }, [selectedIngredient]);


  useEffect(() => {
    if (open) {
      setCurrentUnitComponents(unitComponents || []);
    }
  }, [open, unitComponents]);


  const handleAddComponent = () => {
    if (!selectedIngredient || !quantity) return;

    const selectedUnit = units.length > 0 ? units[0] : null;
    if (!selectedUnit) return;

    const newComponent: UnitComponent = {
      id: Date.now(),
      warehouseProductId: selectedIngredient.id,
      warehouseProductUnitId: selectedUnit.id,
      quantity: parseFloat(quantity),
      modifiers: []
    };

    const updatedComponents = [...currentUnitComponents, newComponent];
    setCurrentUnitComponents(updatedComponents);
    setSelectedIngredient(null);
    setQuantity('');

    if (onUpdate) {
      onUpdate(updatedComponents);
    }
  };

  const handleClose = () => {
    if (onUpdate) {
      onUpdate(currentUnitComponents);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Összetevők</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Button
              variant="text"
              color="primary"
              sx={{ mb: 2, textTransform: 'none' }}
              onClick={handleAddComponent}
              startIcon={<ControlPoint />}
            >
              összetevő hozzáadása
            </Button>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: { sm: 'wrap' },
              gap: 2,
              alignItems: { xs: 'stretch', sm: 'center' },
              mb: 3
            }}>
              <Autocomplete
                options={ingredients}
                getOptionLabel={(option) => option.name || ''}
                onChange={(_, newValue) => setSelectedIngredient(newValue)}
                renderInput={(params) => <TextField {...params} label="Összetevő" />}
                sx={{ width: { xs: '100%', sm: 200 } }}
              />
              <Autocomplete
                options={units}
                getOptionLabel={(option) => option.unitName || ''}
                renderInput={(params) => <TextField {...params} label="Mértékegység" />}
                sx={{ width: { xs: '100%', sm: 150 } }}
                disabled={!selectedIngredient}
              />
              <NumberTextField
                label="Mennyiség"
                value={quantity}
                onChange={setQuantity}
                sx={{ width: { xs: '100%', sm: 100 } }}
              />
              <Button
                variant="text"
                color="primary"
                sx={{ mb: 2, textTransform: 'none', alignSelf: { xs: 'flex-start', sm: 'center' } }}
                onClick={handleAddComponent}
                startIcon={<ControlPoint />}
              >
                módosító hozzáadása
              </Button>
            </Box>

            {currentUnitComponents.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6">Jelenlegi összetevők</Typography>
                {currentUnitComponents.map((component, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      mt: 2
                    }}
                  >
                    <Typography sx={{ wordBreak: 'break-word' }}>
                      ID: {component.id},
                      WarehouseProductId: {component.warehouseProductId},
                      Quantity: {component.quantity}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                      onClick={() => {
                        const updatedComponents = [...currentUnitComponents];
                        updatedComponents.splice(index, 1);
                        setCurrentUnitComponents(updatedComponents);

                        if (onUpdate) {
                          onUpdate(updatedComponents);
                        }
                      }}
                    >
                      Törlés
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: 'stretch', sm: 'center' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
              height: { xs: 'auto', sm: 50 }
            }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleClose}
                sx={{
                  mt: 2,
                  minWidth: { xs: '100%', sm: '15%' },
                  mx: { xs: 0, sm: 1 }
                }}
              >
                Mentés
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={onClose}
                sx={{
                  mt: 2,
                  minWidth: { xs: '100%', sm: '15%' },
                  mx: { xs: 0, sm: 1 }
                }}
              >
                Mégse
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Ingredients;
